import { SubscriberId } from '@benzinga/subscribable';
import { Quote } from './entities';
import { setDifference, setIntersection } from '@benzinga/utils';

export class FieldTracker {
  private subscriptions: Map<SubscriberId, Set<keyof Quote>> = new Map();
  private fields = new Map<keyof Quote, number>();
  private possibleFields: Set<keyof Quote>;
  private updateFields: (fields: Set<keyof Quote>) => void;

  constructor(updateFields: (fields: Set<keyof Quote>) => void, possibleFields: Set<keyof Quote>) {
    this.updateFields = updateFields;
    this.possibleFields = possibleFields;
  }

  public getFields = (): Set<keyof Quote> => {
    return new Set([...this.fields.keys()]);
  };

  public onSubscribe = (id: SubscriberId, fields: Set<keyof Quote> | undefined): void => {
    fields = setIntersection(fields ?? new Set(), this.possibleFields);
    this.subscriptions.set(id, fields);
    this.addField(fields);
  };

  public onUpdate = (id: SubscriberId, fields: Set<keyof Quote> | undefined): void => {
    fields = setIntersection(fields ?? new Set(), this.possibleFields);
    const oldFields = this.subscriptions.get(id);
    if (oldFields) {
      const added = setDifference(fields, oldFields);
      const removed = setDifference(oldFields, fields);

      if (added.size > 0) {
        this.addField(new Set(added));
      }
      if (removed.size > 0) {
        this.removeField(new Set(removed));
      }
      this.subscriptions.set(id, fields);
    } else {
      this.addField(fields);
    }
  };
  public onUnsubscribe = (id: SubscriberId): void => {
    this.removeField(this.subscriptions.get(id) ?? new Set());
    this.subscriptions.delete(id);
  };

  private addField = (fields: Set<keyof Quote>): void => {
    fields.forEach(field => {
      const val = this.fields.get(field) ?? 0;
      this.fields.set(field, val + 1);
    });
    this.updateFields(new Set([...this.fields.keys()]));
  };

  private removeField = (fields: Set<keyof Quote>): void => {
    fields.forEach(field => {
      const count = this.fields.get(field) ?? 0;
      if (count === 1) {
        this.fields.delete(field);
      } else if (count === 0) {
        return;
      } else {
        this.fields.set(field, count - 1);
      }
    });

    this.updateFields(new Set([...this.fields.keys()]));
  };
}
